<script>
import Swal from "sweetalert2";
import Drawer from "@/components/drawer";
import moment from "moment";
import VLazyImage from "v-lazy-image/v2";

// Custom Components
import PageHeader from "@/components/page-header";
import LocalEvent from "@/components/hotel/localEvent.vue";
import UserService from "@/helpers/APIService/user.service";
import Middleware from "@/helpers/middlewares";
import errorHandler from "@/helpers/errorHandler";
import { authComputed } from "@/state/helpers";
import appConfig from "@/app.config";

export default {
  page: {
    title: "Hotel",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  components: {
    PageHeader,
    Drawer,
    LocalEvent,
    VLazyImage,
  },
  props: {
    currentProps: Object,
  },
  data() {
    return {
      editable: true,
      isDragging: false,
      title: "Hotel Local Event",
      table: {
        tableData: [],
        size: 100,
        next: null,
        previous: null,
      },
      openLocalEventDrawer: false,
      selectedLocalEvent: null,
      isEdit: false,
      isDataLoading: true,
      isBtnLoading: false,
      submitted: false,
    };
  },
  computed: {
    ...authComputed,
    localEventProps() {
      return {
        selectedLocalEvent: this.selectedLocalEvent,
        isEdit: this.isEdit,
      };
    },
  },
  mounted() {
    this.getLocalEventData("?size=" + this.table.size);
  },
  methods: {
    // Method is use for get booking list data
    async getLocalEventData(query) {
      this.isDataLoading = true;
      let dataToBeFilter = "";
      if (query !== undefined) {
        dataToBeFilter = query;
      }
      await UserService.getLocalEventList(dataToBeFilter)
        .then((response) => {
          this.table.tableData = response;
        })
        .catch((error) => {
          errorHandler(error);
        });
      this.isDataLoading = false;
    },
    // Method is use for change status
    changeStatus(id, data) {
      UserService.updateLocalEvent(id, data)
        .then(
          (infoAndServices) => {
            this.$emit("submitted", infoAndServices);
            this.isBtnLoading = false;
            this.submitted = false;
            Middleware.notification(
              "Status changed successfully !!",
              "success"
            );
          },
          (error) => {
            this.isBtnLoading = false;
            errorHandler(error);
          }
        )
        .catch((error) => {
          errorHandler(error);
        });
    },
    // Method is use for open and close drawer
    localEventForm() {
      if (this.openLocalEventDrawer == true) {
        this.selectedLocalEvent = null;
        this.isEdit = false;
      }
      this.openLocalEventDrawer = !this.openLocalEventDrawer;
    },
    // Method is use for update live data
    localEventAddedUpdated(data) {
      if (this.isEdit == true) {
        this.table.tableData = this.table.tableData.map((item) => {
          if (item.id === data.id) {
            item = data;
          }
          return item;
        });
      } else {
        this.table.tableData.push(data);
      }
      let message = this.isEdit
        ? "Successfully Updated!"
        : "Successfully Added!";
      Middleware.notification(message, "success");

      this.localEventForm();
    },
    saveAndAddNewLocalEvent(data) {
      this.table.tableData.unshift(data);
      Middleware.notification("Successfully added !!", "success");
    },
    // Method is use for update info and services based on ID
    edit(data) {
      this.selectedLocalEvent = data;
      this.isEdit = true;
      this.localEventForm();
    },
    // Method is use for delete Local Event
    deleteLocalEvent(id, index) {
      Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#238dfa",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Yes, delete it!",
      }).then((result) => {
        if (result.value) {
          UserService.deleteLocalEvent(id).then(
            () => {
              this.table.tableData.splice(index, 1);
              Middleware.notification("Successfully deleted !!", "warning");
            },
            (error) => {
              errorHandler(error);
            }
          );
        }
      });
    },
    // Method is used to format timestamp
    formatTimeStamp(data) {
      return moment.unix(data).format("ddd, MM Do YYYY, h:mm a");
    },
    // Method is used to get more guest
    loadMore: function () {
      if (this.table.next !== null) {
        const str = this.table.next.split("?");
        this.getLocalEventData(`?${str[1]}`);
      }
    },
  },
};
</script>

<template>
  <div>
    <PageHeader
      :title="title"
      :action="localEventForm"
      text="Add New Local Event"
      :isButton="true"
    />
    <div
      class="row"
      v-infinite-scroll="loadMore"
      infinite-scroll-disabled="isDataLoading"
      infinite-scroll-distance="100"
    >
      <div class="col-lg-12">
        <div class="card">
          <div class="card-body">
            <!-- Table -->
            <div class="table-responsive mb-0" v-dragscroll>
              <table
                class="table table-hover mb-0 common-table"
                ref="my-table"
                infinite-scroll-disabled="busy"
                infinite-scroll-distance="10"
              >
                <thead class="table-light">
                  <tr>
                    <th>Name</th>
                    <th>Event Start On</th>
                    <th>Event End On</th>
                    <th>Price</th>
                    <th>Active</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody
                  id="infinite-list"
                  v-if="table.tableData.length > 0 && !isDataLoading"
                >
                  <tr v-for="(data, index) in table.tableData" :key="index">
                    <td @click="edit(data)">
                      <div
                        class="d-flex align-items-center"
                        v-if="data.image !== null"
                      >
                        <v-lazy-image
                          :key="data.id"
                          :src="data.image"
                          class="rounded-circle picture-img"
                          src-placeholder=""
                          alt=""
                        />
                        <span class="text-truncate ms-2 d-block w-75">{{
                          data.name
                        }}</span>
                      </div>
                      <div class="d-flex align-items-center" v-else>
                        <img
                          src="../../../assets/images/placeholder.png"
                          alt=""
                          class="rounded-circle picture-img"
                        /><span class="text-truncate ms-2 d-block w-75">{{
                          data.name
                        }}</span>
                      </div>
                    </td>
                    <td @click="edit(data)">
                      {{ formatTimeStamp(data.start_time) }}
                    </td>
                    <td @click="edit(data)">
                      {{ formatTimeStamp(data.end_time) }}
                    </td>
                    <td @click="edit(data)">
                      {{ data.display_price_label }}
                    </td>
                    <td>
                      <div class="form-check form-switch my-1">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          v-model="data.is_active"
                          @change="
                            changeStatus(data.id, {
                              is_active: data.is_active,
                            })
                          "
                        />
                      </div>
                    </td>
                    <td>
                      <div slot="actions" class="d-flex align-items-center">
                        <i
                          class="fas fa-pencil-alt text-success me-2"
                          title="edit record"
                          @click="edit(data)"
                        ></i>
                        <i
                          class="mdi mdi-trash-can fs-5 text-danger"
                          title="delete record"
                          @click="deleteLocalEvent(data.id, index)"
                        >
                        </i>
                      </div>
                    </td>
                  </tr>
                </tbody>
                <tbody v-if="isDataLoading">
                  <tr v-for="number in 20" :key="number">
                    <td class="placeholder-glow" v-for="no in 6" :key="no">
                      <span class="placeholder w-100 placeholder-lg"></span>
                    </td>
                  </tr>
                </tbody>
                <tbody v-if="!isDataLoading && table.tableData.length == 0">
                  <tr>
                    <td colspan="12" class="text-center">
                      <img
                        src="@/assets/images/empty.svg"
                        class="img-fluid"
                        alt=""
                      />
                      <p>
                        <span class="text-secondary">No Data Found</span>
                      </p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Drawer is for add and update new hotel local events -->
    <Drawer
      @close="localEventForm"
      :align="'right'"
      :closeable="true"
      :maskClosable="true"
      :zIndex="1002"
    >
      <div v-if="openLocalEventDrawer">
        <LocalEvent
          :currentProps="localEventProps"
          @submitted="localEventAddedUpdated"
          @cancelSubmit="localEventForm"
          @saveAndAddNew="saveAndAddNewLocalEvent"
        />
      </div>
    </Drawer>
  </div>
</template>
